import { useEffect, useRef, useState, useCallback } from "react";

import QuestionContainer from "../components/Questionnaire/QuestionContainer";
import { useSwipeable } from "react-swipeable";
import { useMode } from "../context/useMode";
import { useNavigate } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import CreateHandlePopup from "../components/CreateHandlePopup";
import { api } from "../utils/apiHandler";
import { useTranslation } from "react-i18next";
import { useLocale } from "../context/useLocale";
import PageLoader from "./PageLoader";

const QuestionsPage = () => {
  const { t } = useTranslation();
  const { mode } = useMode();
  const { selectedLocale } = useLocale();
  const rawHandle = localStorage.getItem("handle");
  const handle = rawHandle ? JSON.parse(rawHandle) : null;
  const user = handle.user;
  const languageCode = user.language_code;
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [completedPages, setCompletedPages] = useState([]); // for tracking the pages which already answred
  const [isLoading, setIsLoading] = useState(false);

  // for storing the question for all pages, index is page, and it will have the question for the same page
  const [pages, setPages] = useState([]);

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [shakingQuestions, setShakingQuestions] = useState([]);
  const [showPopup, setShowPopup] = useState(false);

  const containerRef = useRef();
  const heightCache = useRef({}); // Cache measured heights

  const apiFormatedAnswer = useRef({});

  const userId = handle?.user?.id;
  const hasFetched = useRef(false);

  // Handle mode check
  useEffect(() => {
    if (!mode) {
      navigate("/choose");
    }
  }, [mode, navigate]);

  // Handle keyboard visibility and viewport changes
  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(window.visualViewport?.height || window.innerHeight);
    };

    // window.visualViewport?.addEventListener("resize", updateViewportHeight);
    // window.addEventListener("resize", updateViewportHeight);
    updateViewportHeight();

    return () => {
      // window -webkit-box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.5) inset;
      // window.visualViewport?.removeEventListener(
      //   "resize",
      //   updateViewportHeight
      // );
      // window.removeEventListener("resize", updateViewportHeight);
    };
  }, []);

  // Dynamic pagination with accurate height measurement
  const measureHeight = useCallback(
    question => {
      if (heightCache.current[question.id]) {
        return heightCache.current[question.id];
      }

      // Replicate QuestionContainer structure
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.visibility = "hidden";
      tempDiv.style.width = "100%";
      tempDiv.style.maxWidth = "786px";
      tempDiv.className = `p-3 ${
        mode === "light" ? "bg-white text-black" : "bg-gray-800 text-white"
      }`;
      tempDiv.innerHTML = `
      <div class="flex flex-col justify-around ">
        <p class="text-lg mb-4">${question.text}</p>
        <div class="flex h-[80px] mt-4">
          ${question.answers
            .map(
              (opt, index) => `
              <label class="flex-1 flex-shrink-0 flex flex-col items-center text-center">
                <input type="radio" name="question-${
                  question.id
                }" value="${opt}" />
                <span class="mt-1 text-xs">${
                  index === 0 || index === 3 || index === 6 ? opt : ""
                }</span>
              </label>
            `,
            )
            .join("")}
        </div>
      </div>
    `;
      document.body.appendChild(tempDiv);
      const height = tempDiv.offsetHeight + 24; // Add margin/padding
      document.body.removeChild(tempDiv);
      heightCache.current[question.id] = height;
      return height;
    },
    [mode],
  );

  const createPages = useCallback(() => {
    const availableHeight = viewportHeight - 65; // Account for header, padding, safe areas
    let tempPages = [];
    let tempPage = [];
    let totalHeight = 0;

    questions.forEach(question => {
      const estHeight = measureHeight(question);

      if (totalHeight + estHeight <= availableHeight) {
        tempPage.push(question);
        totalHeight += estHeight;
      } else {
        if (tempPage.length) tempPages.push(tempPage);
        tempPage = [question];
        totalHeight = estHeight;
      }
    });

    if (tempPage.length) tempPages.push(tempPage);
    setPages(tempPages);
  }, [viewportHeight, questions, measureHeight]);

  useEffect(() => {
    const debounce = (fn, delay) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), delay);
      };
    };

    const debouncedCreatePages = debounce(createPages, 200);
    // window.addEventListener("resize", debouncedCreatePages);
    createPages();
    return () => window.removeEventListener("resize", debouncedCreatePages);
  }, [viewportHeight, questions, createPages]);

  const checkAnsweredInPage = useCallback(
    newAnswers => {
      const completedPages = pages.reduce((acc, pageItem, i) => {
        const isComplete = pageItem.every(
          (q) => newAnswers[q.id] !== undefined,
        );
        if (isComplete) acc.push(i);
        return acc;
      }, []);
      setCompletedPages(completedPages);
      setCurrentPage(completedPages[completedPages.length - 1]);
    },
    [pages],
  );

  const handleAnswer = (questionId, answer) => {
    const newAnswers = { ...answers, [questionId]: answer };
    setAnswers(newAnswers);
    if (mode === "dark")
      localStorage.setItem("darkAnswers", JSON.stringify(newAnswers));
    else localStorage.setItem("lightAnswers", JSON.stringify(newAnswers));

    // tracking all question answred for automatic swiping and updating completed page state
    const allAnswered = pages[currentPage]?.every(
      (q) => newAnswers[q.id] !== undefined,
    );
    if (allAnswered && !completedPages.includes(currentPage)) {
      setCompletedPages(prev => [...prev, currentPage]);
      if (currentPage < pages.length - 1) {
        setCurrentPage(prev => prev + 1);
      }
    }
  };

  const toastFn = () => {
    // Identify unanswered questions on the current page
    const unansweredQuestions = pages[currentPage]
      ?.filter(q => !answers[q.id])
      .map(q => q.id);

    // Set shaking questions
    setShakingQuestions(unansweredQuestions);

    // Trigger vibration on supported devices
    if (navigator.vibrate) {
      navigator.vibrate([100, 50, 100]); // Vibrate pattern: 100ms on, 50ms off, 100ms on
    }
    toast.error("Please complete all questions", {
      position: "bottom-right",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
      transition: Bounce,
    });
    // Clear shaking animation after 1 second
    setTimeout(() => {
      setShakingQuestions([]);
    }, 1000);
  };

  const handlePageChange = newPage => {
    const pageIndex = Math.max(0, Math.min(newPage - 1, pages.length - 1));
    if (pageIndex > currentPage) {
      // Check if all previous pages are completed
      for (let i = currentPage; i < pageIndex; i++) {
        if (!completedPages.includes(i)) {
          toastFn();
          return;
        }
      }
    }
    setCurrentPage(pageIndex);
  };

  const handleSave = async () => {
    const user_answers = Object.entries(answers).map(
      ([question_id, value]) => ({
        question_id,
        value,
      }),
    );
    const payload = { user_answers };

    try {
      await api.post(`/users/${userId}/user_answers/bulk_upsert`, payload);
      if (handle?.user?.first_time_status) setShowPopup(true);
      else navigate("/sangati", { replace: true });
    } catch (error) {
      console.log(error);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (completedPages.includes(currentPage)) {
        setCurrentPage(prev => Math.min(prev + 1, pages.length - 1));
      } else {
        toastFn();
      }
    },
    onSwipedRight: () => {
      setCurrentPage(prev => Math.max(prev - 1, 0));
    },
    trackMouse: true,
  });

  // for fetching question and answer
  const fetchQuestionAndAnswer = useCallback(async () => {
    try {
      setIsLoading(true);
      const [questionRes, answerRes] = await Promise.all([
        api.get(`/questions?mode=${mode}&locale=${languageCode}`),
        api.get(`/users/${userId}/user_answers`),
      ]);
      setQuestions(questionRes.data);
      if (answerRes.data["user_answers"].length) {
        apiFormatedAnswer.current = answerRes.data["user_answers"].reduce(
          (acc, ans) => {
            if (ans.question.mode === mode) {
              acc[ans.question.id] = ans.answer;
            }
            return acc;
          },
          {},
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [mode, userId, languageCode]);

  const getstoredAnswers = useCallback(() => {
    let newAnswers;
    if (Object.keys(apiFormatedAnswer.current)?.length) {
      newAnswers = apiFormatedAnswer.current;
    } else {
      if (mode === "dark") {
        const newAnswersraw = localStorage.getItem("darkAnswers");

        newAnswers = newAnswersraw ? JSON.parse(newAnswersraw) : null;
      } else {
        const newAnswersraw = localStorage.getItem("lightAnswers");
        newAnswers = newAnswersraw ? JSON.parse(newAnswersraw) : null;
      }
    }
    setAnswers(newAnswers ?? {});
    if (newAnswers) {
      checkAnsweredInPage(newAnswers);
    }
  }, [checkAnsweredInPage, mode]);

  // for fetching question and answer
  useEffect(() => {
    if (!hasFetched.current && userId && mode) {
      fetchQuestionAndAnswer();
      hasFetched.current = true;
    }
  }, [fetchQuestionAndAnswer, userId, mode, selectedLocale.value]);

  // storing answers from api or localstorage after pages initialised
  useEffect(() => {
    if (pages.length > 0) {
      getstoredAnswers();
    }
  }, [pages, getstoredAnswers]);

  useEffect(() => {}, [completedPages]);

  return (
    <>
      {isLoading && <PageLoader />}
      <div
        className="h-dvh overflow-hidden bg-bg-grey flex flex-col "
        {...swipeHandlers}
      >
        <div className=" w-full  max-w-[786px] flex-shrink-0 mx-auto h-[60px] px-3 flex ">
          <div className="h-full">
            <button
              className="cursor-pointer h-full flex items-center w-10 "
              onClick={() => navigate(-1)}
            >
              <img src="/icons/backbtn.svg" className="" alt="Back" />
            </button>
          </div>
          <div className="flex-1 flex justify-between items-center gap-1 h-full overflow-hidden ">
            {pages.map((_, index) => (
              <button
                key={index}
                onClick={() => handlePageChange(index + 1)}
                className={` transition-colors flex justify-center flex-1 ${
                  index != 0 && "progress-bar-btn"
                } ${index < completedPages.length && "progressed"}`}
                aria-current={index === currentPage ? "page" : undefined}
                aria-label={`Go to page ${index + 1}`}
              >
                <div
                  className={`size-[9px] rounded-full ${
                    index < completedPages.length
                      ? " bg-[#105142] "
                      : "bg-[#93938D]"
                  }
                  ${index === currentPage && "outline-3 outline-sangati-orange"}
                  `}
                ></div>
              </button>
            ))}
          </div>
        </div>
        <div
          ref={containerRef}
          className="flex transition-transform duration-500 flex-1"
          style={{
            width: `${pages.length * 100}%`,
            transform: `translateX(-${currentPage * (100 / pages.length)}%)`,
          }}
        >
          {pages.map((pageQuestions, index) => (
            <div
              key={index}
              className="w-full px-3 flex flex-col"
              style={{ width: `${100 / pages.length}%` }}
            >
              <div className="flex flex-col gap-4 flex-1">
                {pageQuestions.map(question => (
                  <div key={question.id} className=" overflow-hidden">
                    <QuestionContainer
                      question={question}
                      selectedAnswer={answers[question.id] ?? ""}
                      onAnswer={ans => handleAnswer(question.id, ans)}
                      className={
                        shakingQuestions?.includes(question.id) ? "shake" : ""
                      }
                    />
                  </div>
                ))}
                {index === pages.length - 1 && (
                  <div className="mt-4 mb-2">
                    <button
                      onClick={handleSave}
                      className={`btn-green mx-auto`}
                      aria-label="Save answers"
                    >
                      {t("save")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {showPopup && <CreateHandlePopup setShowPopup={setShowPopup} />}
      </div>
    </>
  );
};

export default QuestionsPage;
